import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Table, Button, Spinner, Alert } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { adminApi, extractError } from '../api/client';
import { fmtRelative } from '../lib/lookups';

function Kpi({ label, value, modifier, icon }) {
  return (
    <Card className={`kpi-card ${modifier ? 'kpi-' + modifier : ''}`} data-testid={`kpi-${modifier || 'default'}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="kpi-label">{label}</div>
            <div className="kpi-value" data-testid={`kpi-value-${modifier || 'default'}`}>{value}</div>
          </div>
          {icon && <i className={`bi ${icon}`} style={{ fontSize: '1.4rem', color: 'var(--muted-fg)' }} />}
        </div>
      </Card.Body>
    </Card>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activity, setActivity] = useState([]);
  const [schedOverview, setSchedOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const [s, r, a, sched] = await Promise.all([
          adminApi.get('/admin/dashboard/stats'),
          adminApi.get('/admin/dashboard/recent-candidates'),
          adminApi.get('/admin/dashboard/recent-activity'),
          adminApi.get('/admin/scheduler/overview').catch(() => ({ data: null })),
        ]);
        if (!active) return;
        setStats(s.data);
        setRecent(r.data || []);
        setActivity(a.data || []);
        setSchedOverview(sched.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of candidate profiles and system activity"
        actions={
          <Link to="/candidates/create">
            <Button variant="primary" data-testid="dashboard-create-candidate-button">
              <i className="bi bi-plus-lg me-1" /> New candidate
            </Button>
          </Link>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center p-5"><Spinner animation="border" /></div>
        ) : (
          <>
            <Row className="g-3 mb-4">
              <Col xs={6} md={4} xl={2}>
                <Kpi label="Total Candidates" value={stats?.candidates_total ?? 0} modifier="total" icon="bi-people" />
              </Col>
              <Col xs={6} md={4} xl={2}>
                <Kpi label="Draft" value={stats?.candidates_by_status?.draft ?? 0} modifier="draft" icon="bi-pencil-square" />
              </Col>
              <Col xs={6} md={4} xl={2}>
                <Kpi label="In Review" value={stats?.candidates_by_status?.in_review ?? 0} modifier="review" icon="bi-eye" />
              </Col>
              <Col xs={6} md={4} xl={2}>
                <Kpi label="Reviewed" value={stats?.candidates_by_status?.reviewed ?? 0} modifier="reviewed" icon="bi-check-circle" />
              </Col>
              <Col xs={6} md={4} xl={2}>
                <Kpi label="Published" value={stats?.candidates_by_status?.published ?? 0} modifier="published" icon="bi-broadcast" />
              </Col>
              <Col xs={6} md={4} xl={2}>
                <Kpi label="API Tokens" value={`${stats?.api_tokens_active ?? 0}/${stats?.api_tokens_total ?? 0}`} modifier="tokens" icon="bi-key" />
              </Col>
            </Row>

            <Row className="g-3">
              <Col xs={12} lg={8}>
                <Card data-testid="dashboard-recent-candidates">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Recently updated candidates</span>
                    <Link to="/candidates" className="text-decoration-none" style={{ fontSize: '0.85rem' }}>
                      View all <i className="bi bi-arrow-right" />
                    </Link>
                  </Card.Header>
                  {recent.length === 0 ? (
                    <div className="empty-state">
                      <i className="bi bi-people" />
                      <div>No candidates yet.</div>
                      <Link to="/candidates/create" className="mt-2 d-inline-block">Create your first candidate</Link>
                    </div>
                  ) : (
                    <Table hover responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Office</th>
                          <th>State</th>
                          <th>Status</th>
                          <th>Updated</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((c) => (
                          <tr key={c.id}>
                            <td className="fw-semibold">{c.full_name}</td>
                            <td className="text-muted">{c.office}</td>
                            <td>{c.state}{c.district ? ` — ${c.district}` : ''}</td>
                            <td><StatusBadge status={c.status} /></td>
                            <td className="text-muted">{fmtRelative(c.updated_at)}</td>
                            <td className="text-end">
                              <Link to={`/candidates/${c.id}`} data-testid={`dashboard-recent-view-${c.id}`}>
                                <Button size="sm" variant="outline-secondary">View</Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card data-testid="dashboard-activity">
                  <Card.Header>Recent activity</Card.Header>
                  <Card.Body className="p-0">
                    {activity.length === 0 ? (
                      <div className="empty-state">
                        <i className="bi bi-clock-history" />
                        <div>No activity yet.</div>
                      </div>
                    ) : (
                      <ul className="list-unstyled mb-0" style={{ maxHeight: 360, overflowY: 'auto' }}>
                        {activity.map((a) => (
                          <li key={a.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: '0.88rem' }}>
                            <div style={{ fontWeight: 600 }}>{a.action}</div>
                            <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                              {a.target_name || a.target_type || '—'} · {fmtRelative(a.timestamp)}
                            </div>
                            {a.actor_email && (
                              <div className="text-muted" style={{ fontSize: '0.78rem' }}>by {a.actor_email}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col xs={12}>
                <Card>
                  <Card.Header>Quick navigation</Card.Header>
                  <Card.Body>
                    <div className="d-flex flex-wrap gap-2">
                      <Link to="/candidates"><Button variant="outline-secondary"><i className="bi bi-people me-1" /> Candidates</Button></Link>
                      <Link to="/search"><Button variant="outline-secondary"><i className="bi bi-search me-1" /> Advanced search</Button></Link>
                      <Link to="/admin/states"><Button variant="outline-secondary"><i className="bi bi-geo-alt me-1" /> States &amp; Counties</Button></Link>
                      <Link to="/tokens"><Button variant="outline-secondary"><i className="bi bi-key me-1" /> API tokens</Button></Link>
                      <Link to="/settings"><Button variant="outline-secondary"><i className="bi bi-gear me-1" /> Settings</Button></Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col xs={12} md={6}>
                <Card data-testid="dashboard-scheduler-upcoming-card">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span><i className="bi bi-calendar-week me-1" /> Upcoming scheduled runs (14d)</span>
                    <Link to="/admin/elections" style={{ fontSize: '0.78rem' }}>Election Days →</Link>
                  </Card.Header>
                  <Card.Body style={{ padding: 0 }}>
                    {!schedOverview || schedOverview.upcoming_runs?.length === 0 ? (
                      <div className="text-muted p-3" style={{ fontSize: '0.88rem' }}>No scheduled runs.</div>
                    ) : (
                      <ul className="list-group list-group-flush">
                        {schedOverview.upcoming_runs.slice(0, 6).map((r) => (
                          <li key={r.id} className="list-group-item d-flex justify-content-between align-items-center" style={{ fontSize: '0.88rem' }}>
                            <span>
                              <strong>{r.name}</strong>
                              <span className="text-muted ms-2">{r.kind || r.parsing_method}</span>
                            </span>
                            <code style={{ fontSize: '0.78rem' }}>{new Date(r.next_run_at).toLocaleString()}</code>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card data-testid="dashboard-scheduler-failures-card">
                  <Card.Header><i className="bi bi-exclamation-triangle me-1" /> Recent failures &amp; reminders</Card.Header>
                  <Card.Body style={{ padding: 0 }}>
                    {(!schedOverview || (schedOverview.recent_failures?.length === 0 && schedOverview.recent_reminders?.length === 0)) ? (
                      <div className="text-muted p-3" style={{ fontSize: '0.88rem' }}>All clear.</div>
                    ) : (
                      <ul className="list-group list-group-flush">
                        {(schedOverview.recent_failures || []).map((f) => (
                          <li key={`f-${f.id}`} className="list-group-item text-danger" style={{ fontSize: '0.85rem' }}>
                            <strong>{f.name}</strong> — {f.last_error?.slice(0, 80) || 'failed'}
                          </li>
                        ))}
                        {(schedOverview.recent_reminders || []).map((r, i) => (
                          <li key={`r-${i}`} className="list-group-item" style={{ fontSize: '0.85rem' }}>
                            <i className="bi bi-bell me-1 text-info" /> <strong>{r.target_name}</strong>
                            <span className="text-muted ms-2">{r.action.replace('election.reminder_', '')}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
}
